import React from 'react';
import { Box, Typography, Stack, IconButton, Chip, Divider, List, ListItem, ListItemText } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { MealLog } from './MealLogDialog';

interface MealLogListProps {
  mealLogs: MealLog[];
  onEdit: (log: MealLog) => void;
  onDelete: (log: MealLog) => void;
}

const getMealTypeLabel = (type: string) => {
  switch (type) {
    case 'BREAKFAST': return 'Breakfast';
    case 'LUNCH': return 'Lunch';
    case 'DINNER': return 'Dinner';
    case 'SNACK': return 'Snack';
    default: return type;
  }
};

const MealLogList: React.FC<MealLogListProps> = ({ mealLogs, onEdit, onDelete }) => {
  return (
    <List disablePadding>
      {mealLogs.map((log, index) => (
        <React.Fragment key={log.id}>
          {index > 0 && <Divider sx={{ my: 1 }} />}
          <ListItem
            disableGutters
            secondaryAction={
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={() => onEdit(log)} color="primary" aria-label="edit meal log">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(log)} color="error" aria-label="delete meal log">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            }
          >
            <ListItemText
              primary={
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {getMealTypeLabel(log.mealType)}:
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                    {log.recipe?.title || log.customName || 'Unnamed Meal'}
                  </Typography>
                </Stack>
              }
              secondary={
                log.ingredients && log.ingredients.length > 0 ? (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                    {log.ingredients.map((ing) => (
                      <Chip key={ing} label={ing} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.75rem' }} />
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    No ingredients listed
                  </Typography>
                )
              }
            />
          </ListItem>
        </React.Fragment>
      ))}
    </List>
  );
};

export default MealLogList;
